import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { createSeriesUrl } from '@/lib/slugs';
import { TrailerModal } from './TrailerModal';
import { Skeleton } from '@/components/ui/skeleton';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface SeriesWithArtists {
  id: string;
  title: string;
  thumbnail_url: string | null;
  trailer_vimeo_id: string | null;
  artists: { id: string; name: string }[];
}

function SeriesCardSkeleton() {
  return (
    <div className="animate-pulse flex-shrink-0 w-[280px] md:w-[calc(33.333%-16px)]">
      <div 
        className="bg-white/5"
        style={{
          height: '200px',
          borderRadius: '10px 10px 0px 0px',
        }}
      />
      <div 
        className="p-4"
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)',
          borderRadius: '0px 0px 10px 10px',
        }}
      >
        <Skeleton className="h-5 w-3/4 mb-2 bg-white/10" />
        <Skeleton className="h-4 w-1/2 bg-white/5" />
      </div>
    </div>
  );
}

export function ExploreCoursesSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sectionRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [series, setSeries] = useState<SeriesWithArtists[]>([]);
  const [loading, setLoading] = useState(true);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  
  // Trailer modal state
  const [trailerModalOpen, setTrailerModalOpen] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState<{ vimeoId: string; title: string } | null>(null);

  const selectedCategory = categories[selectedCategoryIndex] || null;
  
  // For desktop: show 3 at a time, slide by 3
  const itemsPerView = 3;
  const maxSliderIndex = Math.max(0, Math.ceil(series.length / itemsPerView) - 1);
  const canSlideLeft = sliderIndex > 0;
  const canSlideRight = sliderIndex < maxSliderIndex;

  // Track desktop breakpoint
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Trigger visibility with intersection observer at 20% threshold
  useEffect(() => {
    if (loading || series.length === 0) return;
    
    const element = sectionRef.current;
    if (!element) return;
    
    // Check if already in view
    const rect = element.getBoundingClientRect();
    const isAlreadyVisible = rect.top < window.innerHeight * 0.8;
    
    if (isAlreadyVisible) {
      setIsVisible(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [loading, series.length]);

  // Fetch all published series on mount
  useEffect(() => {
    const fetchAllSeries = async () => {
      const { data: seriesData } = await supabase
        .from('series')
        .select('id, title, thumbnail_url, trailer_vimeo_id, artist_id')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (seriesData) {
        const seriesWithArtists = await Promise.all(
          seriesData.map(async (s) => {
            let artists: { id: string; name: string }[] = [];
            if (s.artist_id) {
              const { data: artistData } = await supabase
                .from('artists')
                .select('id, name')
                .eq('id', s.artist_id)
                .maybeSingle();
              if (artistData) {
                artists = [artistData];
              }
            }
            return {
              id: s.id,
              title: s.title,
              thumbnail_url: s.thumbnail_url,
              trailer_vimeo_id: s.trailer_vimeo_id,
              artists
            };
          })
        );
        setSeries(seriesWithArtists);
      }
      setLoading(false);
    };

    fetchAllSeries();
  }, []);

  const handleSeriesClick = (s: SeriesWithArtists) => {
    if (user) {
      navigate(createSeriesUrl(s.id, s.title));
    }
  };

  const handleWatchTrailer = (e: React.MouseEvent, s: SeriesWithArtists) => {
    e.stopPropagation();
    if (s.trailer_vimeo_id) {
      setSelectedTrailer({ vimeoId: s.trailer_vimeo_id, title: s.title });
      setTrailerModalOpen(true);
    }
  };

  const handleSlideLeft = () => {
    setSliderIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSlideRight = () => {
    setSliderIndex((prev) => Math.min(prev + 1, maxSliderIndex));
  };

  // Don't render section if no categories after loading completes
  if (!loading && series.length === 0) {
    return null;
  }
  return (
    <section className="pt-2 md:pt-3 lg:pt-8 pb-2 md:pb-3 lg:pb-8 px-4 sm:px-6 lg:px-8 bg-background mb-0">
      <div 
        ref={sectionRef}
        className="max-w-6xl mx-auto pt-3 md:pt-3 lg:pt-8 pb-6 md:pb-6 lg:pb-16 px-4 sm:px-8"
        style={{
          borderRadius: '140px 140px 0px 0px',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)',
        }}
      >
        {/* Header */}
        <div
          className={cn(
            "relative mb-4 md:mb-8 transition-opacity duration-500 flex flex-col md:flex-row md:items-center md:justify-center",
            isVisible && !loading ? "opacity-100" : "opacity-0"
          )}
        >
          <h2 className="text-headline-3-mobile md:text-headline-3 text-white text-center">
            Explore Courses
          </h2>
          {user && (
            <Button
              variant="ghost"
              className="md:absolute md:right-4 md:top-1/2 md:-translate-y-1/2 text-[16px] font-bold text-foreground hover:bg-transparent hover:text-[#FFFF00] group p-0 transition-colors duration-200 mt-2 md:mt-0 mx-auto md:mx-0"
              onClick={() => navigate('/series')}
            >
              VIEW ALL SERIES
              <ChevronDown className="w-4 h-4 ml-1 text-[#FFFF00] -rotate-90" />
            </Button>
          )}
        </div>

        {/* Series Slider */}
        <div className="relative overflow-hidden min-h-[280px]">
          <div 
            ref={sliderRef}
            className="flex gap-6 transition-transform duration-500 ease-out overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide pb-4 md:pb-0"
            style={isDesktop ? { transform: `translateX(-${sliderIndex * (100 / itemsPerView + 2)}%)` } : undefined}
          >
            {loading || seriesLoading ? (
              // Loading skeletons
              <>
                <SeriesCardSkeleton />
                <SeriesCardSkeleton />
                <SeriesCardSkeleton />
              </>
            ) : (
              series.map((s, index) => (
                <div
                  key={s.id}
                  className={cn(
                    "flex-shrink-0 w-[280px] md:w-[calc(33.333%-16px)] snap-start transition-opacity duration-500",
                    isVisible ? "opacity-100" : "opacity-0"
                  )}
                  style={{
                    transitionDelay: isVisible ? `${400 + (index % 3) * 100}ms` : '0ms',
                  }}
                >
                  <div
                    onClick={() => handleSeriesClick(s)}
                    className={cn(
                      "group relative rounded-[10px] overflow-hidden transition-transform duration-200",
                      user ? "cursor-pointer hover:scale-[1.02]" : "cursor-default"
                    )}
                  >
                  {/* Thumbnail with overlay */}
                  <div 
                    className="relative overflow-hidden"
                    style={{ height: '200px' }}
                  >
                    {s.thumbnail_url ? (
                      <>
                        <img
                          src={s.thumbnail_url}
                          alt={s.title}
                          className={cn(
                            "w-full h-full object-cover transition-transform duration-500",
                            user && "group-hover:scale-105"
                          )}
                        />
                        {/* Dark gradient overlay - always visible */}
                        <div 
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 67.84%, rgba(0, 0, 0, 0.8) 107.45%)',
                          }}
                        />
                      </>
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-foreground">No thumbnail</span>
                      </div>
                    )}
                    
                    {/* Watch Trailer Badge - only show if not logged in and has trailer */}
                    {!user && s.trailer_vimeo_id && (
                      <button
                        onClick={(e) => handleWatchTrailer(e, s)}
                        className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full hover:bg-black/80 transition-colors duration-200 cursor-pointer z-10"
                      >
                        <Play className="w-3 h-3 text-foreground fill-current" />
                        <span className="text-xs text-foreground font-medium">Watch Trailer</span>
                      </button>
                    )}
                  </div>

                  {/* Info box below image */}
                  <div 
                    className="p-4"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)',
                      borderRadius: '0px 0px 10px 10px',
                    }}
                  >
                    <h3 className="text-video-title text-white mb-1 line-clamp-2 md:max-w-[60%]">
                      {s.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {s.artists.length > 0 
                        ? s.artists.map(a => a.name).join(' & ')
                        : 'Unknown Artist'
                      }
                    </p>
                  </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Navigation Arrows - Slider controls (desktop only) */}
        <div
          className={cn(
            "hidden md:flex justify-center gap-4 mt-4 transition-opacity duration-500",
            isVisible && !loading ? "opacity-100" : "opacity-0"
          )}
          style={{
            transitionDelay: isVisible ? '700ms' : '0ms',
          }}
        >
          <button
            onClick={handleSlideLeft}
            disabled={!canSlideLeft}
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200",
              canSlideLeft
                ? "bg-white hover:bg-white/80"
                : "bg-transparent border border-white/50"
            )}
            aria-label="Previous"
          >
            <ChevronLeft className={cn(
              "w-5 h-5 transition-colors duration-200",
              canSlideLeft ? "text-destructive" : "text-white/50"
            )} />
          </button>
          <button
            onClick={handleSlideRight}
            disabled={!canSlideRight}
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200",
              canSlideRight
                ? "bg-white hover:bg-white/80"
                : "bg-transparent border border-white/50"
            )}
            aria-label="Next"
          >
            <ChevronRight className={cn(
              "w-5 h-5 transition-colors duration-200",
              canSlideRight ? "text-destructive" : "text-white/50"
            )} />
          </button>
        </div>
      </div>

      {/* Trailer Modal */}
      <TrailerModal
        isOpen={trailerModalOpen}
        onClose={() => {
          setTrailerModalOpen(false);
          setSelectedTrailer(null);
        }}
        vimeoId={selectedTrailer?.vimeoId || null}
        title={selectedTrailer?.title || ''}
      />
    </section>
  );
}
